// @mui material components
import {
  Snackbar,
  Alert,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Grid,
  ListItem,
  ListItemText,
  Divider,
  Tabs,
  Tab,
  Button,
  ListItemButton,
} from "@mui/material";

import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios.js";

import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDInput from "theme/components/MDInput";
import MDButton from "theme/components/MDButton";
import CellExpressionDialog from "./components/cell_expression_dialog";

import ExcelJS from "exceljs";
import ExcelSheetPreviewer from "./components/excel_preview";
import ScopeBuilderDialog from "./components/scope_builder_dialog";
import AppConfig from "appConfig";

function ExcelReportTemplatePage() {
  const { ref } = useParams();
  const navigate = useNavigate();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [refreshPage, setRefreshPage] = useState(false);

  const [excelTemplate, setExcelTemplate] = useState([]);
  const [formFields, setFormFields] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [mappings, setMappings] = useState({});
  const [loadedWorkBook, setLoadedWorkBook] = useState(null);
  const [activeSheetIdx, setActiveSheetIdx] = useState(0);

  const [scopeMode, setScopeMode] = useState("sheet");
  const [scopeDialogOpen, setScopeDialogOpen] = useState(false);

  function base64DecodeUtf8(base64String) {
    const binary = window.atob(base64String);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binary.charCodeAt(i);
    }

    return bytes.buffer;
  }

  const handleSave = (sheetIndex, cell, expr) => {
    setMappings((prev) => ({
      ...prev,
      [sheetIndex]: {
        ...(prev[sheetIndex] || {}),
        cells: {
          ...(prev[sheetIndex]?.cells || {}),
          [cell]: expr,
        },
      },
    }));
  };

  const handleDeleteMapping = (sheetIndex, cell) => {
    setMappings((prev) => {
      if (!prev?.[sheetIndex]?.cells?.[cell]) return prev;

      const newCells = {
        ...prev[sheetIndex].cells,
      };
      delete newCells[cell];
      const updatedSheet = {
        ...prev[sheetIndex],
        cells: newCells,
      };

      const updated = {
        ...prev,
        [sheetIndex]: updatedSheet,
      };

      if (Object.keys(newCells).length === 0 && !updatedSheet.scopes) {
        delete updated[sheetIndex];
      }
      return updated;
    });
  };

  const handleSaveScope = (scope) => {
    setMappings((prev) => {
      const existingSheet = prev?.[activeSheetIdx] || {};
      return {
        ...prev,
        [activeSheetIdx]: {
          ...existingSheet,
          scopes: {
            ...(existingSheet.scopes || {}),
            [scopeMode]: [...(existingSheet?.scopes?.[scopeMode] || []), scope],
          },
        },
      };
    });
  };

  const handleDeleteScope = (sheetIdx, mode, idx) => {
    setMappings((prev) => {
      if (!prev?.[sheetIdx]?.scopes?.[mode]) return prev;
      const updatedScopes = [...prev[sheetIdx].scopes[mode]];
      updatedScopes.splice(idx, 1);

      return {
        ...prev,
        [sheetIdx]: {
          ...prev[sheetIdx],
          scopes: {
            ...prev[sheetIdx].scopes,
            [mode]: updatedScopes,
          },
        },
      };
    });
  };

  async function presetWorkBook(file) {
    const wb = new ExcelJS.Workbook();
    await wb.xlsx.load(await file.arrayBuffer());
    // console.log(wb);
    setLoadedWorkBook(wb);
  }

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get(`portal/excel/templates/${ref}/fetch.json`)
        .then(async (result) => {
          setShowLoading(false);
          if (result.data.status === "success") {
            setExcelTemplate(result.data.data.report);
            setMappings(result.data.data.report.mappings);
            setFormFields(result.data.data.form_fields);

            let eFile = base64DecodeUtf8(result.data.data.report.excel_template);
            const wb = new ExcelJS.Workbook();
            await wb.xlsx.load(eFile);
            setLoadedWorkBook(wb);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
          setOpen(true);
        });
    };

    fetchData();
  }, [refreshPage]);

  function uploadExcel() {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".xlsx,.xls";

    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) {
        setSeverity("error");
        setErrMsg("No file found.");
        setOpen(true);
        return;
      }

      // Excel Preview
      await presetWorkBook(file);
    };

    input.click();
  }

  async function saveTemplate() {
    if (mappings === null || Object.keys(mappings).length === 0) {
      setSeverity("error");
      setErrMsg("Cells of interest are required.");
      setOpen(true);
      return;
    }
    console.log(mappings);

    setShowLoading(true);
    const json = {
      reference: ref,
      mappings: mappings,
    };
    console.log(json);
    await axios
      .post("portal/excel/templates/update.json", json)
      .then((result) => {
        setShowLoading(false);
        setErrMsg(result.data.data.message);
        if (result.data.status === "success") {
          setSeverity("success");
          setRefreshPage(true);
        } else {
          setSeverity("error");
        }
        setOpen(true);
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  async function deleteReportTemplate() {
    setShowLoading(true);

    await axios
      .get(`portal/excel/templates/${ref}/delete.json`)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          navigate(`/excel-report-templates`);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <CellExpressionDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        cell={selectedCell}
        fields={formFields}
        sheetIndex={selectedSheet}
        onSave={handleSave}
      />
      <ScopeBuilderDialog
        open={scopeDialogOpen}
        mode={scopeMode}
        fields={formFields}
        onSave={handleSaveScope}
        onClose={() => setScopeDialogOpen(false)}
      />
      <MDBox mb={5}>
        <MDBox mt={3} mb={1} display="flex" justifyContent="space-between">
          <MDBox>
            <Button
              color="dark"
              size="small"
              startIcon={<Icon>west</Icon>}
              onClick={() => {
                if (window.history.length > 1) {
                  window.history.back();
                } else {
                  navigate(`/excel-report-templates`);
                }
              }}
            >
              Back
            </Button>
          </MDBox>
          <MDBox>
            <Button
              size="small"
              color="dark"
              iconOnly
              onClick={() => {
                let url = `${AppConfig.api_base_url}portal/excel/templates/generate/${ref}`;
                window.open(url, "_blank");
              }}
              startIcon={
                <Icon sx={{ cursor: "pointer" }} fontSize="small">
                  download
                </Icon>
              }
            >
              Generate Report
            </Button>
          </MDBox>
        </MDBox>
        {excelTemplate && (
          <MDBox>
            {loadedWorkBook && (
              <MDBox>
                <Card>
                  <MDBox p={2}>
                    <MDTypography variant="h6" align="center">
                      Loaded WorkBook
                    </MDTypography>
                    <MDBox mt={1}>
                      <Tabs
                        value={activeSheetIdx}
                        onChange={(e, newIdx) => {
                          setActiveSheetIdx(newIdx);
                        }}
                        variant="scrollable"
                        scrollButtons="auto"
                      >
                        {loadedWorkBook.worksheets.map((sheet, idx) => (
                          <Tab
                            key={idx}
                            label={
                              <MDTypography
                                variant="h6"
                                fontWeight="regular"
                                fontSize={14}
                                align="center"
                              >
                                {sheet.name}
                              </MDTypography>
                            }
                          />
                        ))}
                      </Tabs>
                    </MDBox>
                    <MDBox mt={3} mb={5}>
                      <ExcelSheetPreviewer
                        worksheet={loadedWorkBook.worksheets[activeSheetIdx]}
                        mappedCells={mappings?.[activeSheetIdx]?.cells}
                        onCellClick={(cell) => {
                          setSelectedCell(cell);
                          setSelectedSheet(activeSheetIdx);
                          setDialogOpen(true);
                        }}
                      />
                    </MDBox>
                    <MDBox mb={3}>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={5} lg={5}>
                          <MDBox>
                            <MDTypography variant="h6" align="left">
                              Cells
                            </MDTypography>
                            <Divider />
                            {mappings?.[activeSheetIdx]?.cells ? (
                              <>
                                {Object.entries(mappings?.[activeSheetIdx]?.cells || {}).map(
                                  ([cellAddr, expr]) => (
                                    <MDBox>
                                      <Grid container spacing={3}>
                                        <Grid item xs={6} md={3} lg={3}>
                                          <MDTypography
                                            variant="h6"
                                            fontSize={14}
                                            fontWeight="light"
                                            pt={2}
                                          >
                                            {cellAddr}
                                          </MDTypography>
                                        </Grid>
                                        <Grid item xs={6} md={9} lg={9}>
                                          <MDBox align="right">
                                            <MDTypography
                                              variant="h6"
                                              fontSize={13}
                                              fontWeight="regular"
                                            >
                                              {expr ?? ""}
                                            </MDTypography>

                                            {expr && (
                                              <>
                                                <MDButton
                                                  size="small"
                                                  onClick={() =>
                                                    handleDeleteMapping(activeSheetIdx, cellAddr)
                                                  }
                                                  startIcon={<Icon>clear</Icon>}
                                                >
                                                  Remove
                                                </MDButton>
                                              </>
                                            )}
                                          </MDBox>
                                        </Grid>
                                      </Grid>
                                      <Divider />
                                    </MDBox>
                                  )
                                )}
                              </>
                            ) : (
                              <MDTypography variant="subtitle2" fontSize={13}>
                                No cells mapped. Click on any cell to map it.
                              </MDTypography>
                            )}
                          </MDBox>
                        </Grid>
                        <Grid item xs={12} md={2} lg={2} align="center">
                          <Divider orientation="vertical" />
                        </Grid>
                        <Grid item xs={12} md={5} lg={5}>
                          <MDBox>
                            <MDBox display="flex" justifyContent="space-between">
                              <MDTypography variant="h6">Sheet Grouping</MDTypography>
                              <MDButton
                                size="small"
                                onClick={() => {
                                  setScopeMode("sheet");
                                  setScopeDialogOpen(true);
                                }}
                                iconOnly
                                variant="contained"
                                color="dark"
                              >
                                <Icon>add</Icon>
                              </MDButton>
                            </MDBox>
                            <MDBox>
                              <Divider />
                            </MDBox>
                            <MDBox>
                              {mappings?.[activeSheetIdx]?.scopes?.sheet?.length > 0 ? (
                                <>
                                  {mappings?.[activeSheetIdx]?.scopes?.sheet?.map((scope, idx) => (
                                    <ListItem>
                                      <ListItemText
                                        primary={
                                          <MDTypography variant="subtitle2" fontSize={14}>
                                            {scope.by || scope.byExpr}
                                          </MDTypography>
                                        }
                                      />
                                      <MDBox>
                                        <MDButton
                                          onClick={() => {
                                            if (window.confirm("Delete entry?") === false) {
                                              return;
                                            }
                                            handleDeleteScope(activeSheetIdx, "sheet", idx);
                                          }}
                                        >
                                          <Icon>clear</Icon>
                                        </MDButton>
                                      </MDBox>
                                    </ListItem>
                                  ))}
                                </>
                              ) : (
                                <MDTypography variant="subtitle2" fontSize={13}>
                                  No sheet grouping added.
                                </MDTypography>
                              )}
                            </MDBox>
                          </MDBox>
                          <MDBox mt={5}>
                            <MDBox display="flex" justifyContent="space-between">
                              <MDTypography variant="h6">Row Cascades</MDTypography>
                              <MDButton
                                size="small"
                                onClick={() => {
                                  setScopeMode("row");
                                  setScopeDialogOpen(true);
                                }}
                                iconOnly
                                variant="contained"
                                color="dark"
                              >
                                <Icon>add</Icon>
                              </MDButton>
                            </MDBox>
                            <MDBox>
                              <Divider />
                            </MDBox>
                            <MDBox>
                              {mappings?.[activeSheetIdx]?.scopes?.row?.length > 0 ? (
                                <>
                                  {mappings?.[activeSheetIdx]?.scopes?.row?.map((scope, idx) => (
                                    <ListItem>
                                      <ListItemText
                                        primary={
                                          <MDTypography variant="subtitle2" fontSize={14}>
                                            {scope.by || scope.byExpr}
                                          </MDTypography>
                                        }
                                      />
                                      <MDBox>
                                        <MDButton
                                          onClick={() => {
                                            if (window.confirm("Delete entry?") === false) {
                                              return;
                                            }
                                            handleDeleteScope(activeSheetIdx, "row", idx);
                                          }}
                                        >
                                          <Icon>clear</Icon>
                                        </MDButton>
                                      </MDBox>
                                    </ListItem>
                                  ))}
                                </>
                              ) : (
                                <MDTypography variant="subtitle2" fontSize={13}>
                                  No row cascades added
                                </MDTypography>
                              )}
                            </MDBox>
                          </MDBox>
                        </Grid>
                      </Grid>
                    </MDBox>
                  </MDBox>
                </Card>
              </MDBox>
            )}
            <MDBox mt={5}>
              <Card sx={{ height: "100%", marginTop: "0px" }}>
                <MDBox p={3}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6} lg={6}>
                      <MDBox>
                        <MDBox mt={0} align="left">
                          <MDTypography variant="h6">{excelTemplate.title}</MDTypography>
                          <MDTypography variant="h6" fontSize={14} fontWeight="regular">
                            {excelTemplate.campaign}
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={6} lg={6}>
                      <MDBox>
                        <MDBox align="right">
                          <MDButton
                            variant="outlined"
                            color="error"
                            onClick={() => {
                              if (window.confirm(`Delete this template?`) === true) {
                                deleteReportTemplate();
                              }
                            }}
                            startIcon={<Icon>delete</Icon>}
                          >
                            Delete Template
                          </MDButton>
                          &nbsp;&nbsp;
                          <MDButton
                            variant="contained"
                            color="dark"
                            onClick={() => {
                              saveTemplate();
                            }}
                            startIcon={<Icon>check</Icon>}
                          >
                            Save Changes
                          </MDButton>
                        </MDBox>
                      </MDBox>
                    </Grid>
                  </Grid>
                </MDBox>
              </Card>
            </MDBox>
          </MDBox>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default ExcelReportTemplatePage;
